import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';

/**
 * The one segmented switch of the app (List | Calendar, a project's Tasks · Docs · Chats on
 * phones, Mine | Waiting on Home, By date | By bot...): a track with a hairline border (rounded-lg)
 * and the chosen segment raised in it (rounded-md), as the design draws it: in dark mode a lighter
 * segment on a dark track (muted and accent are the same grey there, so neither is used for it). `items`: [{ key, label, icon?, count?, to? }]; an item
 * with `to` is a link (replace), else a button calling onChange(key). `fill` stretches the
 * segments across the width (`'phone'`: only below md); `badge` is an attention count.
 */
export default function Segmented({ items, value, onChange, fill = false, size = 'md', className, label, testId }) {
  const tall = size === 'lg';
  return (
    <div role="tablist" aria-label={label} data-testid={testId}
      className={cn('inline-flex gap-0.5 p-[3px] rounded-lg border border-border bg-muted dark:bg-card', fill === true && 'flex w-full',
        fill === 'phone' && 'max-md:flex max-md:w-full', className)}>
      {items.map(({ key, label: text, icon: Icon, count, badge, to, testId: itemTestId }) => {
        const on = value === key;
        const classes = cn(
          'min-w-0 inline-flex items-center justify-center gap-1.5 rounded-md px-3 whitespace-nowrap transition-colors',
          tall ? 'h-8 text-[13.5px]' : 'h-7 text-[13px]',
          fill === true && 'flex-1 px-1.5', fill === 'phone' && 'max-md:flex-1 max-md:px-1.5',
          on ? 'bg-card text-foreground font-medium shadow-sm dark:bg-white/[0.12] dark:shadow-none' : 'text-muted-foreground hover:text-foreground',
        );
        const inner = (
          <>
            {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />}
            <span className="truncate">{text}</span>
            {count != null && <span className="font-normal text-muted-foreground tabular-nums">{count}</span>}
            {badge > 0 && (
              <span className="min-w-[15px] h-[15px] rounded-full bg-attention-soft text-orange-700 dark:text-attention-text text-[10px] font-semibold leading-[15px] px-1">
                {badge}
              </span>
            )}
          </>
        );
        return to ? (
          <Link key={key} to={to} replace role="tab" aria-selected={on} data-testid={itemTestId} className={classes}>{inner}</Link>
        ) : (
          <button key={key} type="button" role="tab" aria-selected={on} data-testid={itemTestId} onClick={() => onChange?.(key)} className={classes}>
            {inner}
          </button>
        );
      })}
    </div>
  );
}
